import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Repeat, Shield, Users } from 'lucide-react';

const categories = [
  { id: 'electronics', name: 'Electronics', image: 'https://images.unsplash.com/photo-1515940279136-2f419eea8051?w=400&h=300&fit=crop' },
  { id: 'clothing', name: 'Clothing', image: 'https://images.unsplash.com/photo-1624222244232-5f1ae13bbd53?w=400&h=300&fit=crop' },
  { id: 'furniture', name: 'Furniture', image: 'https://images.unsplash.com/photo-1586939731615-bd3396cbd440?w=400&h=300&fit=crop' },
  { id: 'books', name: 'Books', image: 'https://images.unsplash.com/photo-1511881346740-60bcc5769e35?w=400&h=300&fit=crop' },
  { id: 'sports', name: 'Sports', image: 'https://images.pexels.com/photos/4498606/pexels-photo-4498606.jpeg?w=400&h=300&fit=crop' },
  { id: 'home-garden', name: 'Home & Garden', image: 'https://images.unsplash.com/photo-1556733820-a45a2f25f124?w=400&h=300&fit=crop' },
  { id: 'toys', name: 'Toys', image: 'https://images.pexels.com/photos/31061848/pexels-photo-31061848.jpeg?w=400&h=300&fit=crop' },
  { id: 'other', name: 'Other', image: 'https://images.unsplash.com/photo-1586866714717-b0475ff4c7da?w=400&h=300&fit=crop' },
];

const features = [
  {
    icon: Repeat,
    title: 'Simple Trading',
    description: 'List your items, browse others, and propose trades with just a few clicks.'
  },
  {
    icon: Shield,
    title: 'Safe & Secure',
    description: 'Message other traders only after initiating a trade request.'
  },
  {
    icon: Users,
    title: 'Community Driven',
    description: 'Connect with local traders and give your unused items a new home.'
  }
];

export default function HomePage() {
  return (
    <div className="animate-fadeIn">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F9F9F7] via-[#F5F5F4] to-[#E7E5E4] opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#008080]/10 text-[#008080] rounded-full text-sm font-medium mb-6">
                No money. Just trades.
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight mb-6">
                Trade what you have for what you need
              </h1>
              <p className="text-lg text-stone-600 mb-8 max-w-2xl leading-relaxed">
                CUESS is a barter-based platform where you trade what you have for what you need. No money involved. Just meaningful trades.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/browse">
                  <Button 
                    size="lg" 
                    className="bg-[#008080] hover:bg-[#008080]/90 text-white rounded-full px-8 h-12 text-base"
                    data-testid="hero-browse-btn"
                  >
                    Start Browsing
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="rounded-full px-8 h-12 text-base border-stone-300"
                    data-testid="hero-signup-btn"
                  >
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#008080]/10 rounded-3xl transform rotate-3"></div>
                <img 
                  src="https://images.pexels.com/photos/7007167/pexels-photo-7007167.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Woman opening a package at home"
                  className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">How it works</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Trading on CUESS is simple and straightforward
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-8 rounded-2xl bg-[#F9F9F7] border border-stone-100 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#008080] text-white flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-3">{feature.title}</h3>
                <p className="text-stone-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl font-bold text-stone-900 mb-2">Browse by Category</h2>
              <p className="text-stone-600">Find exactly what you're looking for</p>
            </div>
            <Link to="/browse" className="hidden md:block">
              <Button variant="ghost" className="rounded-full" data-testid="view-all-categories-btn">
                View All
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category) => (
              <Link 
                key={category.id}
                to={`/browse?category=${category.id}`}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden"
                data-testid={`category-${category.id}`}
              >
                <img 
                  src={category.image}
                  alt={category.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-semibold text-lg">{category.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#008080]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to start trading?
          </h2>
          <p className="text-[#E7E5E4] text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of traders who are exchanging items every day. 
            Create your free account and list your first item in minutes.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-[#008080] hover:bg-stone-100 rounded-full px-8 h-12 text-base font-medium"
              data-testid="cta-signup-btn"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900 text-stone-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-2xl font-bold text-white">CUESS</span>
              <p className="text-sm mt-1">Trade what you have for what you need</p>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link to="/browse" className="hover:text-white transition-colors">Browse</Link>
              <Link to="/register" className="hover:text-white transition-colors">Sign Up</Link>
              <Link to="/login" className="hover:text-white transition-colors">Log In</Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-stone-800 text-center text-sm">
            <p>&copy; 2025 CUESS. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
